import { useCallback, useEffect, useState } from "react";
import {
  Presentation, Calculator, GraduationCap, Loader2, CheckCircle2, X,
  Sparkles, Coins, BadgeCheck,
} from "lucide-react";
import { toast } from "sonner";
import AppShell from "../components/AppShell";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const NAV = [
  { to: "/onboarding", label: "Onboarding" },
  { to: "/founder/dashboard", label: "Dashboard" },
];

const STAGES = [
  "Idea / pre-revenue",
  "Early revenue (under KES 10M/yr)",
  "Growing (KES 10M – 50M/yr)",
  "Established (over KES 50M/yr)",
];

const MASTERCLASSES = [
  {
    kind: "pitch_deck",
    title: "Pitch Deck Building",
    icon: Presentation,
    blurb: "Build an investor-grade pitch deck with Doreen Zaki — structure, storytelling, and the slides funders actually read.",
  },
  {
    kind: "financial_model",
    title: "Financial Model Building",
    icon: Calculator,
    blurb: "Build a defensible financial model — assumptions, projections, and the numbers funders will interrogate.",
  },
];

function StatusBadge({ status }) {
  if (status === "active") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-bold text-emerald-800">
        <BadgeCheck className="h-3 w-3" /> Active
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2.5 py-0.5 text-[11px] font-bold text-sky-800">
      <CheckCircle2 className="h-3 w-3" /> Interest registered
    </span>
  );
}

function RegisterModal({ kind, kindLabel, askStage, defaults, onClose, onDone }) {
  const [name, setName] = useState(defaults.name || "");
  const [email, setEmail] = useState(defaults.email || "");
  const [company, setCompany] = useState(defaults.company || "");
  const [stage, setStage] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim() || !email.trim()) { toast.error("Name and email are required."); return; }
    if (askStage && !stage) { toast.error("Select your current business stage."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/masterclasses/register", {
        kind, name, email, company_name: company, business_stage: stage,
      });
      onDone(data.registration);
      toast.success("Interest registered — we'll be in touch!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Registration failed. Try again.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="masterclass-register-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose} role="dialog" aria-modal="true">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-[#0A8B85]">Register my interest</p>
            <h3 className="mt-1 text-xl font-extrabold text-neutral-900">{kindLabel}</h3>
          </div>
          <button onClick={onClose} data-testid="masterclass-register-close" aria-label="Close"
            className="rounded-md p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-4 space-y-3">
          <div>
            <label className="block text-xs font-semibold text-neutral-700" htmlFor="mc-name">Your name</label>
            <input id="mc-name" value={name} onChange={(e) => setName(e.target.value)}
              data-testid="masterclass-register-name"
              className="mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm focus:border-[#0A8B85] focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-neutral-700" htmlFor="mc-email">Email</label>
            <input id="mc-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="masterclass-register-email"
              className="mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm focus:border-[#0A8B85] focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-neutral-700" htmlFor="mc-company">Company name</label>
            <input id="mc-company" value={company} onChange={(e) => setCompany(e.target.value)}
              data-testid="masterclass-register-company"
              className="mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm focus:border-[#0A8B85] focus:outline-none" />
          </div>
          {askStage && (
            <div>
              <label className="block text-xs font-semibold text-neutral-700" htmlFor="mc-stage">Current stage of business</label>
              <select id="mc-stage" value={stage} onChange={(e) => setStage(e.target.value)}
                data-testid="masterclass-register-stage"
                className="mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm focus:border-[#0A8B85] focus:outline-none">
                <option value="">Select…</option>
                {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          )}
        </div>

        <button onClick={submit} disabled={busy}
          data-testid="masterclass-register-submit"
          className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0A8B85] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#066667] disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
          Register my interest
        </button>
      </div>
    </div>
  );
}

const KIND_META = {
  pitch_deck: { kind: "pitch_deck", label: "Pitch Deck Building", askStage: false },
  financial_model: { kind: "financial_model", label: "Financial Model Building", askStage: false },
  crp: { kind: "crp", label: "Capital Readiness Program (ISBI Strathmore)", askStage: true },
};

export default function Masterclasses() {
  const { user } = useAuth();
  const [regs, setRegs] = useState({});
  const [companyName, setCompanyName] = useState("");
  const [modal, setModal] = useState(null); // {kind, label, askStage}
  const [buying, setBuying] = useState("");
  const [paidBanner, setPaidBanner] = useState(false);

  const loadMine = useCallback(async () => {
    try {
      const { data } = await api.get("/masterclasses/mine");
      const byKind = {};
      (data.registrations || []).forEach((r) => { byKind[r.kind] = r; });
      setRegs(byKind);
    } catch { /* not fatal */ }
  }, []);

  useEffect(() => {
    document.title = "Masterclasses & Programs | Clinq";
    loadMine();
    // Deep-link support for campaign emails:
    //   ?register=pitch_deck|financial_model|crp → opens the interest form
    //   ?focus=<kind> or #<kind>                → scrolls to that card
    const params = new URLSearchParams(window.location.search);
    const registerKind = params.get("register");
    const focusKind = params.get("focus") || (window.location.hash || "").replace("#", "");
    api.get("/companies/me")
      .then(({ data }) => setCompanyName(data?.company_name || ""))
      .catch(() => {})
      .finally(() => {
        if (registerKind && KIND_META[registerKind]) setModal(KIND_META[registerKind]);
      });
    const scrollTarget = KIND_META[registerKind] ? registerKind : focusKind;
    if (scrollTarget && KIND_META[scrollTarget]) {
      setTimeout(() => {
        document.querySelector(`[data-testid="masterclass-card-${scrollTarget}"]`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 500);
    }
    // Post-Paystack return: verify payment, then refresh registration states.
    const sid = params.get("reference") || params.get("trxref");
    if (sid) {
      window.history.replaceState({}, "", "/masterclasses");
      api.post(`/founder/credits/poll/${sid}`)
        .then(({ data }) => {
          if (data?.payment_status === "paid") {
            setPaidBanner(true);
            setTimeout(loadMine, 1200);
          }
        })
        .catch(() => {});
    }
  }, [loadMine]);

  const purchase = async (kind) => {
    setBuying(kind);
    try {
      const { data } = await api.post("/founder/credits/checkout", {
        origin_url: window.location.origin, masterclass: kind,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed. Try again.");
      setBuying("");
    }
  };

  const defaults = { name: user?.name || "", email: user?.email || "", company: companyName };

  return (
    <AppShell nav={NAV}>
      <div className="mx-auto max-w-4xl px-4 py-8 sm:py-10" data-testid="masterclasses-page">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#0A8B85]">Learn with Clinq</p>
        <h1 className="mt-1 text-3xl font-extrabold text-neutral-900 sm:text-4xl">Masterclasses &amp; Programs</h1>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-neutral-600">
          Hands-on sessions run personally by Doreen Zaki. Masterclasses are reserved for founders
          actively building their investor materials — once you&apos;ve purchased credits and are actively
          building, the masterclasses themselves are <b>completely free of charge</b>.
        </p>

        {paidBanner && (
          <div data-testid="masterclass-paid-banner"
            className="mt-5 flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
            <div className="text-sm text-emerald-900">
              <b>Payment confirmed — you&apos;re in!</b> Your masterclass registration is now active.
              Our team will email you the next session details.
            </div>
            <button onClick={() => setPaidBanner(false)} aria-label="Dismiss"
              className="ml-auto rounded-md p-1 text-emerald-500 hover:bg-emerald-100">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Masterclasses */}
        <div className="mt-8 grid gap-5 sm:grid-cols-2">
          {MASTERCLASSES.map((m) => {
            const Icon = m.icon;
            const reg = regs[m.kind];
            return (
              <div key={m.kind} data-testid={`masterclass-card-${m.kind}`}
                className="flex flex-col rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm">
                <div className="flex items-center justify-between">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#E5EEF1] text-[#00828F]">
                    <Icon className="h-5 w-5" />
                  </span>
                  {reg && <StatusBadge status={reg.status} />}
                </div>
                <h2 className="mt-4 text-lg font-extrabold text-neutral-900">{m.title}</h2>
                <p className="mt-1.5 flex-1 text-sm leading-relaxed text-neutral-600">{m.blurb}</p>
                <p className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#0A8B85]">
                  <Sparkles className="h-3.5 w-3.5" /> Free once you&apos;re actively building with credits
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button onClick={() => setModal({ kind: m.kind, label: m.title, askStage: false })}
                    data-testid={`masterclass-interest-${m.kind}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#0A8B85] px-4 py-2 text-xs font-bold text-[#0A8B85] hover:bg-[#E5EEF1]">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Register my interest
                  </button>
                  <button onClick={() => purchase(m.kind)} disabled={buying === m.kind}
                    data-testid={`masterclass-purchase-${m.kind}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#0A8B85] px-4 py-2 text-xs font-bold text-white hover:bg-[#066667] disabled:opacity-50">
                    {buying === m.kind ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Coins className="h-3.5 w-3.5" />}
                    Purchase credits
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Capital Readiness Program */}
        <div data-testid="masterclass-card-crp"
          className="mt-6 overflow-hidden rounded-2xl bg-gradient-to-br from-[#00303F] via-[#0A4543] to-[#00828F] p-6 text-white sm:p-8">
          <div className="flex items-center justify-between gap-3">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-white/10">
              <GraduationCap className="h-5 w-5" />
            </span>
            {regs.crp && <StatusBadge status={regs.crp.status} />}
          </div>
          <p className="mt-4 text-[10px] font-bold uppercase tracking-[0.18em] text-[#4FB0BB]">
            In partnership with ISBI Strathmore Business School
          </p>
          <h2 className="mt-1 text-2xl font-extrabold">Capital Readiness Program</h2>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-white/80">
            Go deeper than documents. This program helps founders understand how to strategically
            leverage capital, build a business that attracts funders, and position themselves for
            the right conversations at the right time.
          </p>
          <button onClick={() => setModal({ kind: "crp", label: "Capital Readiness Program (ISBI Strathmore)", askStage: true })}
            data-testid="masterclass-interest-crp"
            className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-white px-5 py-2.5 text-xs font-bold text-[#00303F] hover:bg-neutral-100">
            <CheckCircle2 className="h-3.5 w-3.5" /> Register my interest
          </button>
        </div>
      </div>

      {modal && (
        <RegisterModal kind={modal.kind} kindLabel={modal.label} askStage={modal.askStage}
          defaults={defaults}
          onClose={() => setModal(null)}
          onDone={(reg) => { setRegs((r) => ({ ...r, [reg.kind]: reg })); setModal(null); }} />
      )}
    </AppShell>
  );
}
